import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subscription, interval, startWith, switchMap } from 'rxjs';
import { IncidenciaService } from 'app/modules/admin/apps/incidencia/incidencia.service';

interface IncidenciaStats {
    total: number;
    abiertas: number;
    porEstatus: Record<string, number>;
    porTipo: Record<string, number>;
}

@Component({
    selector: 'incidencia-badge',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
    template: `
        <button
            mat-icon-button
            (click)="irAIncidencias()"
            [matTooltip]="tooltip"
            class="relative"
            data-testid="incidencia-badge">
            <mat-icon [svgIcon]="'heroicons_outline:exclamation-circle'"></mat-icon>
            @if (pendientes > 0) {
                <span
                    class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white shadow"
                    [ngClass]="badgeClass"
                    data-testid="incidencia-badge-count">
                    {{ pendientes > 99 ? '99+' : pendientes }}
                </span>
            }
        </button>
    `,
})
export class IncidenciaBadgeComponent implements OnInit, OnDestroy {
    pendientes = 0;
    total = 0;
    ultimasResueltas = 0;
    cargando = true;
    error = false;

    private pollSub?: Subscription;

    constructor(
        private _incidenciaService: IncidenciaService,
        private _router: Router,
        private _cdr: ChangeDetectorRef,
    ) {}

    get badgeClass(): string {
        if (this.pendientes >= 50) return 'bg-red-600';
        if (this.pendientes >= 20) return 'bg-amber-500';
        if (this.pendientes >= 1) return 'bg-amber-500';
        return 'bg-gray-400';
    }

    get tooltip(): string {
        if (this.cargando) return 'Cargando incidencias…';
        if (this.error) return 'Error al cargar incidencias';
        if (this.pendientes === 0) {
            return `Sin incidencias pendientes (${this.total} total)`;
        }
        return `${this.pendientes} incidencia(s) pendiente(s) — click para revisar`;
    }

    ngOnInit(): void {
        // Polling cada 60s, empieza inmediatamente.
        this.pollSub = interval(60_000)
            .pipe(
                startWith(0),
                switchMap(() => this._incidenciaService.getStats()),
            )
            .subscribe({
                next: (s: any) => {
                    this.cargando = false;
                    this.error = false;
                    this.pendientes =
                        s?.abiertas ??
                        s?.porEstatus?.PENDIENTE ??
                        s?.porEstatus?.['PENDIENTE'] ??
                        0;
                    this.total = s?.total ?? 0;
                    this._cdr.detectChanges();
                },
                error: () => {
                    this.cargando = false;
                    this.error = true;
                    this._cdr.detectChanges();
                },
            });
    }

    irAIncidencias(): void {
        this._router.navigate(['/apps/incidencia/list']);
    }

    ngOnDestroy(): void {
        this.pollSub?.unsubscribe();
    }
}
